/**
 * Auth Store - Zustand-based authentication state management
 * Replaces React Context with persistent, performant state management
 */

import type { ApiError } from "@shared-types/api.types"
import type { AxiosError } from "axios"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"
import type { AuthResponse } from "@/features/auth/services/auth.service"
import * as authService from "@/features/auth/services/auth.service"

/**
 * Profile photo interface
 */
export interface ProfilePhoto {
  id: string
  model: string
  model_id: string
  file_name: string
  file_type: string
  filePath: string
  file_ext_storage_id: string | null
  isCover: boolean
  is_cover_image: boolean
  created_by: string
  updated_by: string
  created_at: string
  updated_at: string
}

/**
 * Profile tag interface
 */
export interface ProfileTag {
  id: string
  name: string
}

/**
 * User interface matching backend response
 */
export interface User {
  id: string
  email: string
  name?: string
  username?: string
  city?: string
  bio?: string
  timezone?: string
  useDeviceTimezone?: boolean
  phoneNumber?: string
  countryCode?: string
  emailVerified: boolean
  profileCreated: boolean
  onboardingCompleted: boolean
  loginType?: string
  hasSetPassword?: boolean

  // Complete profile data
  street_address?: string
  state?: string
  country?: string
  zip_code?: string
  description?: string
  website_url?: string
  twitter_url?: string
  instagram_url?: string
  facebook_url?: string

  // Additional profile fields
  availableForCoaching?: boolean

  // Relations
  role?: {
    id: string
    name: string
    description?: string
  }
  userSettings?: {
    id: string
    calendarEnabled: boolean
    notificationsEnabled: boolean
    contactsEnabled: boolean
    locationEnabled: boolean
    showPracticedRituals: boolean
    showAttendedEvents: boolean
    showCoachInfo: boolean
    showStatsAndStreaks: boolean
    allowProfileView: boolean
    languagePreferences?: "en" | "es"
  }
  // Note: userDevices is excluded as it's sensitive device session data
  // Additional profile fields
  profileVisibility?: "public" | "private"
  profilePictureUrl?: string
  latitude?: number | null
  longitude?: number | null

  // Privacy/Visibility settings
  // Original settings are strings ("everyone" | "friends"), computed access is boolean
  practice_rituals_visibility_access?: string | boolean
  teaching_rituals_visibility_access?: string | boolean
  attended_events_visibility_access?: string | boolean
  stats_visibility_access?: string | boolean
  coach_visibility_access?: string | boolean

  // Spiritual traditions, ethnic backgrounds, and intentions
  spiritual_traditions?: Array<{ id: string; title: string; is_custom?: boolean }>
  ethnic_backgrounds?: Array<{ id: string; title: string; is_custom?: boolean }>
  intentions?: Array<{ id: string; title: string; description: string }>

  // Follow status fields (only included when viewing another user's profile)
  isFollowing?: boolean
  isPending?: boolean

  // Relations
  tags?: ProfileTag[]
  profile_photos?: ProfilePhoto[]
  // Introduction video URL for avatar overlay
  introVideoUrl?: string
  // Note: userDevices is excluded as it's sensitive device session data
}

/**
 * Auth Store State Interface
 */
interface AuthState {
  // State
  user: User | null
  accessToken: string | null
  isLoading: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<AuthResponse>
  signup: (data: {
    email: string
    name: string
    username: string
    city: string
    password: string
    referralCode?: string
    streetAddress?: string | null
    state?: string | null
    zipcode?: string | null
    country?: string | null
    latitude?: number | null
    longitude?: number | null
  }) => Promise<void>
  logout: () => Promise<void>
  refreshToken: () => Promise<void>
  setUser: (user: User | null) => void
  clearError: () => void

  // Role/Status methods
  isAuthenticated: () => boolean
  isProfileCreated: () => boolean
  isOnboardingCompleted: () => boolean
}

/**
 * Auth Store with localStorage persistence
 * Only persists non-sensitive data (user object, not tokens)
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      isLoading: false,
      error: null,

      /**
       * Login action
       * Authenticates user and updates store state
       */
      login: async (email, password) => {
        set({ isLoading: true, error: null })
        try {
          const response: AuthResponse = await authService.login({
            email,
            password,
          })
          // response.user is already transformed by the service
          set({ user: response.user, isLoading: false })
          return response
        } catch (error) {
          const err = error as AxiosError<ApiError>
          const errorMessage = err.response?.data?.message || "Login failed"
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      /**
       * Signup action
       * Creates new user account with profile data and stores tokens
       */
      signup: async data => {
        console.log("[AuthStore] signup called with:", { ...data, password: "[REDACTED]" })
        set({ isLoading: true, error: null })
        try {
          console.log("[AuthStore] calling authService.signup...")
          const response: AuthResponse = await authService.signup(data)
          console.log("[AuthStore] signup response user:", response.user)
          set({ user: response.user, isLoading: false })
        } catch (error) {
          console.error("[AuthStore] signup error:", error)
          const err = error as AxiosError<ApiError>
          const errorMessage = err.response?.data?.message || "Signup failed"
          set({ error: errorMessage, isLoading: false })
          throw error
        }
      },

      /**
       * Logout action
       * Clears user data and tokens from store and storage
       */
      logout: async () => {
        try {
          await authService.logout()
        } catch (error) {
          console.error("Logout error:", error)
        } finally {
          set({ user: null, accessToken: null })
        }
      },

      /**
       * Refresh token action
       * Token refresh is primarily handled by axios interceptor
       * This method is available for manual refresh if needed
       */
      refreshToken: async () => {
        // Token refresh is handled by axios interceptor in api-client.ts
        console.log("Token refresh handled by axios interceptor")
      },

      /**
       * Set user manually
       * Useful for updating user data without full authentication flow
       */
      setUser: user => set({ user }),

      /**
       * Clear error state
       */
      clearError: () => set({ error: null }),

      /**
       * Check if user is authenticated
       */
      isAuthenticated: () => !!get().user,

      /**
       * Check if user has created profile
       */
      isProfileCreated: () => !!get().user?.profileCreated,

      /**
       * Check if user has completed onboarding
       */
      isOnboardingCompleted: () => !!get().user?.onboardingCompleted,
    }),
    {
      name: "rituality-auth-store",
      storage: createJSONStorage(() => localStorage),
      // Only persist non-sensitive data
      // Tokens are managed by token-manager.ts
      partialize: state => ({
        user: state.user,
        // NOT: accessToken, refreshToken (security)
      }),
    },
  ),
)
